/**
 * 推理队列（Alice 页面 queue 的复刻）：LLM 与生图的运行载荷、排队深度、最近任务。
 *
 * 背景：Alice 有服务端 FIFO 队列和可改的并发配置；本仓网关用 Redis 准入（渠道并发租约加优先级让路），
 *   没有排队表，所以「队列配置」卡片改成只读的准入规则读数，没有保存按钮；并发上限在渠道与 API Key 上设置。
 *   数据来自 /control/admin/inference-queue：运行中与排队中是数据面登记的进行中请求，其余是最近一小时的用量记录。
 * 设计意图：卡片、筛选栏、表格的 DOM 与列顺序保持 Alice 原样；每 3 秒静默刷新，页面不可见时暂停；
 *   上一轮没返回就不发下一轮，避免慢查询叠起来。
 * 关键约束：自动刷新只带当前筛选，每轮工作量固定（最多 100 行）；刷新失败只在首次加载时提示。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { InferenceQueue, QueueItem, QueueStatus } from '@ap/api-client/admin-runtime'
import { runtimeApi } from '@ap/api-client/admin-runtime'
import { AG_runtime as G, errorTextRuntime } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, useToast } from '@ap/ui'
import { OpsPage, Stat, Stats, TableWrap, Toolbar } from '../../alice/kit.tsx'
import { fmtDuration, fmtInt, useInterval } from '../../alice/kit-runtime.tsx'
import { EmptyRow, PILL, Pill, Section, ToolSelect, ellipsis, type PillTone } from '../../alice/kit-runtime2.tsx'

const POLL_MS = 3000
const PAGE = 100

const TONE: Record<QueueStatus, PillTone> = {
  queued: ['#c2410c', '#fff7ed'],
  running: ['#1d4ed8', '#eff6ff'],
  completed: ['#047857', '#ecfdf5'],
  failed: ['#b91c1c', '#fef2f2'],
  cancelled: ['#4b5563', '#f3f4f6'],
}

export function Queue() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [pool, setPool] = useState('')
  const [status, setStatus] = useState('')
  const [data, setData] = useState<InferenceQueue | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState('')
  const [stamp, setStamp] = useState('')
  const busy = useRef(false)
  const seq = useRef(0)

  const load = useCallback(
    async (silent: boolean) => {
      if (busy.current) return
      busy.current = true
      const my = ++seq.current
      try {
        const d = await runtimeApi.inferenceQueue({ pool_type: pool, status, limit: PAGE })
        if (my !== seq.current) return
        setData(d)
        setFailed('')
        setStamp(new Date().toLocaleTimeString())
      } catch (e) {
        console.error('[queue] 加载失败', e)
        if (!silent) {
          const msg = errorTextRuntime(e, locale)
          setFailed(msg)
          toast.error(msg)
        }
      } finally {
        busy.current = false
        setLoading(false)
      }
    },
    [pool, status, toast, locale],
  )

  useEffect(() => {
    setLoading(true)
    void load(false)
  }, [load])

  useInterval(() => {
    if (!document.hidden) void load(true)
  }, POLL_MS)

  const llm = data?.summary.llm
  const image = data?.summary.image
  const wait = (ms?: number) => t(G.queue.oldestWait, { v: fmtDuration(ms) })
  const cap = (avg?: number) => t(G.queue.capacitySub, { avg: fmtDuration(avg) })
  const items = data?.items ?? []

  return (
    <OpsPage
      eyebrow={t(G.queue.eyebrow)}
      title={t(G.queue.title)}
      desc={t(G.queue.desc)}
      chip={t(G.queue.refreshState, { time: stamp || '—' })}
    >
      <Stats>
        <Stat label={t(G.queue.llmRunning)} value={llm ? `${llm.running} / ${llm.max_concurrent}` : '—'} sub={cap(llm?.avg_wait_ms)} />
        <Stat label={t(G.queue.llmQueued)} value={llm ? fmtInt(llm.queued) : '—'} sub={wait(llm?.oldest_wait_ms)} />
        <Stat label={t(G.queue.imageRunning)} value={image ? `${image.running} / ${image.max_concurrent}` : '—'} sub={cap(image?.avg_wait_ms)} />
        <Stat label={t(G.queue.imageQueued)} value={image ? fmtInt(image.queued) : '—'} sub={wait(image?.oldest_wait_ms)} />
      </Stats>

      <Section title={t(G.queue.configTitle)} copy={t(G.queue.configCopy)}>
        <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {(data?.config.priorities ?? []).map((p) => (
            <div key={p.id}>
              {t(p.max_wait_s > 0 ? G.queue.priorityRule : G.queue.priorityNoWait, { id: p.id.toUpperCase(), cap: p.cap_percent, wait: p.max_wait_s })}
            </div>
          ))}
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {t(G.queue.capRule)} · {t(G.queue.windowRule, { n: data?.config.window_minutes ?? 60 })}
          </div>
        </div>
      </Section>

      <Toolbar count={t(G.queue.totalBadge, { n: fmtInt(data?.total ?? 0) })}>
        <ToolSelect
          value={pool}
          onChange={(e) => setPool(e.target.value)}
          options={[
            { value: '', label: t(G.queue.allTypes) },
            { value: 'llm', label: t(G.queue.llm) },
            { value: 'image', label: t(G.queue.image) },
          ]}
        />
        <ToolSelect
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: '', label: t(G.queue.allStatus) },
            { value: 'queued', label: t(G.queue.queued) },
            { value: 'running', label: t(G.queue.running) },
            { value: 'completed', label: t(G.queue.completed) },
            { value: 'failed', label: t(G.queue.failed) },
            { value: 'cancelled', label: t(G.queue.cancelled) },
          ]}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void load(false)}>
          {t(G.queue.refreshNow)}
        </button>
      </Toolbar>

      <TableWrap
        head={
          <>
            <th>{t(G.queue.colStatus)}</th>
            <th>{t(G.queue.colKind)}</th>
            <th>{t(G.queue.colQueuedAt)}</th>
            <th>{t(G.queue.colUser)}</th>
            <th>{t(G.queue.colModel)}</th>
            <th>{t(G.queue.colWait)}</th>
            <th>{t(G.queue.colRun)}</th>
            <th>{t(G.queue.colRequest)}</th>
            <th>{t(G.queue.colNode)}</th>
            <th>{t(G.queue.colError)}</th>
          </>
        }
      >
        {loading && !data ? (
          <EmptyRow cols={10}>{t(G.common.loading)}</EmptyRow>
        ) : failed && !data ? (
          <EmptyRow cols={10}>{t(G.common.loadFailed, { msg: failed })}</EmptyRow>
        ) : items.length === 0 ? (
          <EmptyRow cols={10}>{t(G.queue.empty)}</EmptyRow>
        ) : (
          items.map((r) => <Row key={`${r.request_id}:${r.status}`} row={r} locale={locale} />)
        )}
      </TableWrap>
    </OpsPage>
  )
}

function Row({ row, locale }: { row: QueueItem; locale: 'ko' | 'zh' | 'en' }) {
  const { t } = useI18n()
  const label = t(G.queue[row.status])
  return (
    <tr>
      <td>
        <Pill tone={TONE[row.status] ?? PILL.gray}>{label}</Pill>
      </td>
      <td>
        <b style={{ fontSize: 12 }}>{row.pool_type === 'image' ? t(G.queue.image) : t(G.queue.llm)}</b>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{row.job_type}</div>
      </td>
      <td style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDateTime(row.queued_at, locale)}</td>
      <td>
        {row.nickname ? (
          <>
            {row.nickname}
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
              {row.user_id}
            </div>
          </>
        ) : (
          <span className="mono" style={{ fontSize: 11 }}>
            {row.user_id || '—'}
          </span>
        )}
      </td>
      <td className="mono" style={{ fontSize: 11, maxWidth: 180, ...ellipsis }} title={row.model_id}>
        {row.model_id || '—'}
      </td>
      <td style={{ fontSize: 12 }}>{fmtDuration(row.current_wait_ms)}</td>
      <td style={{ fontSize: 12 }}>{row.current_run_ms === null ? '—' : fmtDuration(row.current_run_ms)}</td>
      <td className="mono" style={{ fontSize: 10 }} title={row.request_id}>
        {row.request_id}
      </td>
      <td className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
        {row.worker_id || '—'}
      </td>
      <td style={{ maxWidth: 180 }}>
        {row.error_message ? (
          <span style={{ fontSize: 11, color: 'var(--error-text)' }} title={row.error_message}>
            {row.error_message.slice(0, 45)}
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  )
}
